import { inQuietHours } from './time'
import { quietHoursEndAll, phoneCountry } from './zones'

/**
 * SMS rules that are pure functions (design 6.2, 17.5): opt-out keywords, carrier error codes
 * in plain words, retry timing inside contact hours, GSM-7 segments, each country's rules, and log masking.
 */

// ---------------------------------------------------------------------------- keywords
export type KeywordAction = 'opt_out' | 'opt_in' | 'help'
export interface KeywordLists { opt_out: string[]; opt_in: string[]; help: string[] }

/** Normalises a message the way carriers do: trim, upper-case, collapse spaces, drop trailing . ! ?. */
export function keywordForm(text: string) {
  return String(text ?? '').trim().toUpperCase().replace(/\s+/g, ' ').replace(/[.!?]+$/, '').trim()
}

/**
 * A keyword is the whole message, never a word inside a sentence: "please stop texting me" is a
 * conversation for the AI and a person, "STOP" is a keyword. Returns null for anything else.
 */
export function detectKeyword(text: string, lists: KeywordLists): { action: KeywordAction; keyword: string } | null {
  const k = keywordForm(text)
  if (!k || k.length > 20) return null
  const has = (l: string[]) => l.some((w) => keywordForm(w) === k)
  if (has(lists.opt_out)) return { action: 'opt_out', keyword: k }
  if (has(lists.opt_in)) return { action: 'opt_in', keyword: k }
  if (has(lists.help)) return { action: 'help', keyword: k }
  return null
}

/** The honest default replies, adapted to consent by purpose. */
export function keywordReply(action: KeywordAction, scope: 'marketing' | 'all', company: string, custom?: string | null) {
  if (custom?.trim()) return custom.replace(/\{company\}/g, company)
  if (action === 'opt_out') {
    return scope === 'all'
      ? `${company}: you won't get any more texts from us on this number. Reply START if you change your mind.`
      : `${company}: we've received your request and won't send you marketing texts. Our team will review it. As this number also sends service messages about things you have with us, we may still need to contact you about those.`
  }
  if (action === 'opt_in') return `${company}: thanks, we've noted your request to receive messages again. Reply STOP at any time to opt out.`
  return `${company}: this number sends messages about your account with us. Reply with your question and our team will help. Reply STOP to opt out.`
}

// ---------------------------------------------------------------------------- carrier errors
export type DeliveryClass = 'transient' | 'permanent' | 'opted_out' | 'unknown'

/** Codes worth another attempt: the network or handset was momentarily unable, not unwilling. */
const TRANSIENT = new Set(['30001', '30003', '30008', '30022', '30023', '30453', '21611', '30009'])
/** Codes where the same text to the same number cannot work: pause, do not retry. */
const PERMANENT = new Set(['30002', '30004', '30005', '30006', '30007', '30010', '30034', '30450', '21211', '21408', '21606', '21614', '21617'])
/** The customer opted out at the carrier: suppress, as if they had replied STOP to us. */
const OPTED_OUT = new Set(['21610'])
/** The number itself cannot take texts: suppressing it stops every future attempt. */
const UNREACHABLE = new Set(['30005', '30006', '21211', '21614'])

const TWILIO_MEANING: Record<string, string> = {
  '30001': 'too many messages were queued at once at the provider',
  '30002': 'the messaging account is suspended',
  '30003': 'the phone was off or out of coverage',
  '30004': 'the message was blocked by the carrier or by the customer',
  '30005': 'the number does not exist or is no longer active',
  '30006': 'the number is a landline or its carrier cannot receive texts',
  '30007': 'the carrier filtered the message as spam',
  '30008': 'the carrier failed the message without giving a reason',
  '30009': 'part of the message went missing on the way',
  '30010': 'the message cost more than the price limit set at the provider',
  '30022': 'a US carrier rate limit was hit',
  '30023': 'the provider\'s daily message cap was reached',
  '30034': 'the sending number is not registered with the carrier',
  '30450': 'the provider blocked delivery',
  '30453': 'the provider\'s fraud guard blocked it',
  '21211': 'the customer\'s phone number is not valid',
  '21408': 'sending to that country is not enabled on the provider account',
  '21606': 'our sending number cannot send texts',
  '21610': 'the customer has blocked our number (they replied STOP to the carrier)',
  '21611': 'the provider\'s outgoing queue was full',
  '21614': 'the customer\'s number is not a mobile',
  '21617': 'the message was too long',
}

/** Telnyx error codes we see on messaging (their 4xxxx range). */
const TELNYX: Record<string, { cls: DeliveryClass; meaning: string; unreachable?: boolean }> = {
  '40001': { cls: 'permanent', meaning: 'the number is a landline or cannot receive texts', unreachable: true },
  '40002': { cls: 'permanent', meaning: 'the carrier blocked the message as spam' },
  '40003': { cls: 'permanent', meaning: 'the carrier blocked the message' },
  '40008': { cls: 'transient', meaning: 'the carrier failed the message without giving a reason' },
  '40300': { cls: 'opted_out', meaning: 'the customer has blocked our number (they replied STOP to the carrier)' },
  '40310': { cls: 'permanent', meaning: 'the customer\'s phone number is not valid', unreachable: true },
  '40311': { cls: 'permanent', meaning: 'the number is not a mobile', unreachable: true },
  '40018': { cls: 'transient', meaning: 'a carrier rate limit was hit' },
}

export interface CarrierError { code: string | null; cls: DeliveryClass; meaning: string; unreachable: boolean; provider: string }

/** Classifies a carrier error code and says what it means in the words an operator needs. */
export function carrierError(provider: string, code: string | number | null | undefined): CarrierError {
  const c = code === null || code === undefined || code === '' ? null : String(code).replace(/\D/g, '') || null
  const p = provider === 'telnyx' ? 'telnyx' : 'twilio'
  if (!c) return { code: null, cls: 'unknown', meaning: 'the carrier gave no reason', unreachable: false, provider: p }
  if (p === 'telnyx') {
    const t = TELNYX[c]
    if (t) return { code: c, cls: t.cls, meaning: t.meaning, unreachable: Boolean(t.unreachable), provider: p }
    return { code: c, cls: 'unknown', meaning: `an unrecognised carrier error (look up Telnyx code ${c})`, unreachable: false, provider: p }
  }
  const cls: DeliveryClass = OPTED_OUT.has(c) ? 'opted_out' : PERMANENT.has(c) ? 'permanent' : TRANSIENT.has(c) ? 'transient' : 'unknown'
  return { code: c, cls, meaning: TWILIO_MEANING[c] ?? `an unrecognised carrier error (look up Twilio code ${c})`, unreachable: UNREACHABLE.has(c), provider: p }
}

/** "The number does not exist or is no longer active (Twilio 30005)". */
export function describeCarrierError(provider: string, code: string | number | null | undefined) {
  const e = carrierError(provider, code)
  const s = e.meaning.charAt(0).toUpperCase() + e.meaning.slice(1)
  return e.code ? `${s} (${e.provider === 'telnyx' ? 'Telnyx' : 'Twilio'} ${e.code})` : s
}

/** Pulls a code out of stored text such as "Not delivered (code 30005)" or "twilio error 30005". */
export function errorCodeIn(text: string | null | undefined): string | null {
  const m = String(text ?? '').match(/\b(2\d{4}|3\d{4}|4\d{4})\b/)
  return m ? m[1] : null
}

// ---------------------------------------------------------------------------- retry timing
/**
 * When to retry a text that failed for a reason that can clear (+4h, then +12h). The retry lands
 * outside quiet hours in every one of the customer's zones. A retry pushed beyond the horizon (counted
 * from the failure) is walked back to the last contact-hours slot inside it, because a retry that the
 * stale-message guard would refuse is worse than a slightly shorter wait. Null: no slot exists, pause.
 */
export function retryAt(failedAt: Date, delayHours: number, quiet: { start: string; end: string } | null, zones: string[], horizonHours: number): Date | null {
  const horizon = new Date(failedAt.getTime() + horizonHours * 3_600_000)
  let at = new Date(failedAt.getTime() + delayHours * 3_600_000)
  if (at > horizon) at = horizon
  if (!quiet || !zones.length) return at
  const pushed = quietHoursEndAll(at, quiet, zones)
  if (pushed.getTime() <= horizon.getTime()) return pushed
  // Walk back in 15-minute steps to the latest instant that is outside quiet hours everywhere.
  for (let t = at.getTime(); t > failedAt.getTime() + 30 * 60_000; t -= 15 * 60_000) {
    const d = new Date(t)
    if (!zones.some((z) => inQuietHours(d, quiet, z))) return d
  }
  return null
}

// ---------------------------------------------------------------------------- segments
const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const GSM_EXT = '^{}\\[~]|€\f'
const BASIC = new Set([...GSM_BASIC])
const EXT = new Set([...GSM_EXT])

/** The encoding and segment count a carrier will bill, with GSM-7 extension characters counted twice. */
export function smsEncoding(text: string): { encoding: 'GSM-7' | 'UCS-2'; units: number; segments: number } {
  const chars = [...text]
  if (chars.every((c) => BASIC.has(c) || EXT.has(c))) {
    const units = chars.reduce((n, c) => n + (EXT.has(c) ? 2 : 1), 0)
    return { encoding: 'GSM-7', units, segments: units <= 160 ? 1 : Math.ceil(units / 153) }
  }
  // UCS-2 counts UTF-16 code units: an emoji takes two.
  const units = text.length
  return { encoding: 'UCS-2', units, segments: units <= 70 ? 1 : Math.ceil(units / 67) }
}

// ---------------------------------------------------------------------------- each country's rules
export interface CountryRules {
  country: string
  /** Hours by law or regulator code when we may not text proactively, in the customer's zone. */
  legal_quiet: { start: string; end: string } | null
  /** Commercial messages must say who sent them. */
  sender_identification: boolean
  /** Commercial messages must carry a working opt-out. */
  unsubscribe_required: boolean
  /** Countries that accept an alphanumeric sender (registration may be needed). */
  alpha_sender: boolean
  law: string
}

const RULES: Record<string, CountryRules> = {
  AU: { country: 'AU', legal_quiet: null, sender_identification: true, unsubscribe_required: true, alpha_sender: true, law: 'Spam Act 2003 (identify the sender, functional unsubscribe honoured within 5 working days)' },
  GB: { country: 'GB', legal_quiet: null, sender_identification: true, unsubscribe_required: true, alpha_sender: true, law: 'PECR (identify the sender, a simple free way to opt out of marketing)' },
  IE: { country: 'IE', legal_quiet: null, sender_identification: true, unsubscribe_required: true, alpha_sender: true, law: 'ePrivacy Regulations 2011' },
  NZ: { country: 'NZ', legal_quiet: null, sender_identification: true, unsubscribe_required: true, alpha_sender: false, law: 'Unsolicited Electronic Messages Act 2007' },
  US: { country: 'US', legal_quiet: { start: '21:00', end: '08:00' }, sender_identification: true, unsubscribe_required: true, alpha_sender: false, law: 'TCPA (no texts before 8am or after 9pm local time; STOP honoured)' },
  CA: { country: 'CA', legal_quiet: { start: '21:30', end: '09:00' }, sender_identification: true, unsubscribe_required: true, alpha_sender: false, law: 'CASL (identify the sender, unsubscribe honoured within 10 business days)' },
}
const DEFAULT_RULES: CountryRules = { country: '', legal_quiet: null, sender_identification: true, unsubscribe_required: true, alpha_sender: false, law: 'No country-specific rule known: identify the sender and offer an opt-out' }

export function countryRules(country: string | null | undefined): CountryRules {
  const c = String(country ?? '').toUpperCase()
  return RULES[c] ?? { ...DEFAULT_RULES, country: c }
}
export function rulesForNumber(e164: string | null | undefined, fallbackCountry: string) {
  return countryRules(phoneCountry(e164) ?? fallbackCountry)
}

/**
 * The stricter of two overnight quiet windows (e.g. a company's 20:00-08:00 and a law's 21:00-08:00):
 * the earlier start and the later end. A daytime window cannot be merged safely and is kept as given.
 */
export function strictestQuiet(a: { start: string; end: string } | null, b: { start: string; end: string } | null) {
  if (!a) return b
  if (!b) return a
  const overnight = (w: { start: string; end: string }) => w.start > w.end
  if (!overnight(a) || !overnight(b)) return a
  return { start: a.start < b.start ? a.start : b.start, end: a.end > b.end ? a.end : b.end }
}

/** An alphanumeric sender ID: 1 to 11 characters, letters, digits, spaces and + - & _ ., at least one letter. */
export function isAlphaSender(s: string | null | undefined) {
  const v = String(s ?? '')
  return /^[A-Za-z0-9 +\-&_.]{1,11}$/.test(v) && /[A-Za-z]/.test(v)
}

/**
 * Wording a proactive text must carry: who it is from, and how to opt out. With an alphanumeric sender the
 * customer cannot reply, so the opt-out names a number they can text instead.
 */
export function complianceText(body: string, o: {
  company: string; opt_out: string | null; alpha_sender: boolean; reply_number: string | null; commercial: boolean; rules: CountryRules
}) {
  let text = body.trim()
  const names = o.company.toLowerCase().split(/\s+/).filter((w) => w.length > 2)
  if (o.commercial && o.rules.sender_identification && !names.some((w) => text.toLowerCase().includes(w))) text = `${o.company}: ${text}`
  if (o.opt_out && !/\bSTOP\b/.test(text)) {
    const line = o.alpha_sender ? (o.reply_number ? `To opt out, text STOP to ${o.reply_number}` : null) : o.opt_out
    if (line) text = `${text}\n${line}`
  }
  return text
}

// ---------------------------------------------------------------------------- log hygiene
/** "+1 555•• ••678": enough to recognise a number in a log, not enough to use it. */
export function maskPhone(v: string | null | undefined) {
  const d = String(v ?? '')
  if (d.length < 6) return '•••'
  return `${d.slice(0, 4)}${'•'.repeat(Math.max(0, d.length - 7))}${d.slice(-3)}`
}
export function maskEmail(v: string | null | undefined) {
  const [u, dom] = String(v ?? '').split('@')
  if (!dom) return '•••'
  return `${u.slice(0, 1)}•••@${dom}`
}
/** Masks every phone number and email address in a log line. */
export function maskContacts(text: string) {
  return String(text ?? '')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, (m) => maskEmail(m))
    .replace(/\+?\d[\d\s-]{7,}\d/g, (m) => maskPhone(m.replace(/[\s-]/g, '')))
}

